/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import msdyn365Commerce from '@msdyn365-commerce/core';
import { ICheckoutViewProps, IEmailDelivery, ILineItem, ILineItemDeliveryGroup, ILineItems, IPickUpAtStore } from '@msdyn365-commerce-modules/checkout';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { PriceComponent } from '@msdyn365-commerce/components';
import * as MsDyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { ICheckoutProps } from '../definition-extensions/checkout.ext.props.autogenerated';


export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => (
    <Node {...EmailDelivery}>
        {label}
    </Node>
);

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({ LineItems, Header, heading, editLink, itemsForPickup, itemsForShip, itemsForEmail }) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
    </Node>
);

function _renderOtherCharges(props: ICheckoutViewProps): React.ReactNode | null{
    const chargeAmountInclTax = props.data.checkout.result?.checkoutCart.cart.ChargeAmount && props.data.checkout.result?.checkoutCart.cart.ChargeAmount || 0;
    const shippingCharges = props.data.checkout.result?.checkoutCart.cart.ShippingChargeAmount && props.data.checkout.result?.checkoutCart.cart.ShippingChargeAmount || 0;
    const chargeLines = props.data.checkout.result?.checkoutCart.cart.ChargeLines;

    let otherChargesAmountFromLines = 0;
    let totalChargeAmountFromLines = 0;
    let chargesOverriden = false;
    let displayOtherChargesAmount = 0;
    let shippingChargesFromLines = 0;
    const freightChargeCode = props.context.app.config.freightChargeCode;

    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
            const chargeAmountInclTaxLocal = chargeLine.CalculatedAmount ? chargeLine.CalculatedAmount : 0;
            if (chargeLine.ChargeCode != freightChargeCode) {
                otherChargesAmountFromLines += chargeAmountInclTaxLocal;
            }
            if (chargeLine.ChargeCode == freightChargeCode){
                shippingChargesFromLines += chargeAmountInclTaxLocal;
            }
        });
    }

    if(shippingChargesFromLines == 0 &&  shippingCharges != shippingChargesFromLines)
    {
        totalChargeAmountFromLines = shippingCharges + otherChargesAmountFromLines;
    }
    else{
        totalChargeAmountFromLines = shippingChargesFromLines + otherChargesAmountFromLines;
    }
    
    chargesOverriden = totalChargeAmountFromLines != chargeAmountInclTax;
    displayOtherChargesAmount = chargesOverriden ? 0 : otherChargesAmountFromLines;
    if (displayOtherChargesAmount == 0) {
        return null;
    }

    return (
        <p className={`msc-order-summary__line_other-charges`}>
           {displayOtherChargesAmount && displayOtherChargesAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>Other charges</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: displayOtherChargesAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}


function _renderShippingInclTaxes(props: ICheckoutViewProps): React.ReactNode | null{
    const chargeLines = props.data.checkout.result?.checkoutCart.cart.ChargeLines;
    let chargeAmountInclTax = 0;
    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
            if (chargeLine.IsShipping) {
                const chargeAmountInclTaxLocal = chargeLine.GrossAmount ? chargeLine.GrossAmount : 0;
                chargeAmountInclTax += chargeAmountInclTaxLocal;
            }
        });
    }
    if (chargeAmountInclTax == 0) {
        return props.orderSummary?.lines?.shipping;
    }

    return (
        <p className={`msc-order-summary__line-shipment`}>
           {chargeAmountInclTax && chargeAmountInclTax > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.shippingLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: chargeAmountInclTax }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderTotalSavings(props: ICheckoutViewProps): JSX.Element | null {

    const DiscountAmount = props.data.checkout.result?.checkoutCart.cart.DiscountAmount;
    return (
        <p className={`msc-order-summary__line--discount-amount`}>
            {DiscountAmount && DiscountAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.totalSavingsLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: DiscountAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

const _renderOrderSummarylines = (props: ICheckoutViewProps & ICheckoutProps<{}>): JSX.Element | null => {
    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
    const {orderSummary } = props;
    if (!orderSummary) {
        return null;
    }
    return (
        <div className='msc-order-summary-wrapper'>
        {orderSummary.heading}
        <div className='msc-order-summary__items'>
            {orderSummary.lines && (
                <>
                    {_renderOurGiftToYouMessage(props)}
                    {isB2B ? orderSummary.lines.subtotal : _renderSubTotal(props)}
                    {isB2B ? null : orderSummary.lines.totalDiscounts}
                    {_renderShippingInclTaxes(props)}
                    {_renderOtherCharges(props)}
                    {orderSummary.lines.otherCharge}
                    {/*hide on us*/}
                    {props.config.hideTaxLine ? null : orderSummary.lines.tax}
                    {_renderTotalOrder(props)}
                    {orderSummary.lines.loyalty}
                    {orderSummary.lines.giftCard}
                    {isB2B ? null : orderSummary.lines.orderTotal}
                    {isB2B? null : _renderTotalSavings(props)}
                </>
            )}
        </div>
        {_renderFreeShippingMessage(props)}
        {_getCheckoutSummaryText(props)}
    </div>
    );
};

function _renderProgressBar(percentFilled: number): JSX.Element | null {
    return <>
        <div className="citta-progress-bar">
            <div className="citta-progress-bar__progress" style={{width:`${percentFilled}%`}}></div>
        </div>
    </>
}
function _renderOurGiftToYouMessage(props: ICheckoutViewProps & ICheckoutProps<{}>): JSX.Element | null {

    var ogtyAmount = 0;
    var ogtyItems = props.data.checkout.result?.checkoutCart.cart.CartLines?.filter(line => line.DiscountLines && line.DiscountLines[0] && line.DiscountLines[0].OfferName === props.config.ogtyOfferName && line.ItemId !== "GiftCard");
    ogtyItems && ogtyItems.forEach(item => {
        ogtyAmount += item.NetPrice!;
    });
     var noneOgtyItems = props.data.checkout.result?.checkoutCart.cart.CartLines?.filter(line => line.DiscountLines && line.DiscountLines.length == 0 && line.ItemId !== "GiftCard");
    noneOgtyItems && noneOgtyItems.forEach(item => {
        ogtyAmount += item.NetPrice!;
    });
    
    if (!props.config.ourGiftToYou || props.data.checkout.result?.checkoutCart.cart.CartLines?.some(line => line.ItemId === "GiftCard")) {
        return _renderFreeShippingMessage(props);
    }
     if(props.config.ourGiftToYou && ogtyAmount > 0){
        if(ogtyAmount < 100) {
            return <div className='cart-bar'>
            {_renderProgressBar(ogtyAmount)}
            <div className="shipping-to-free-message" style={{marginBottom:"30px"}}>
                <b>Spend & Save:</b> Spend another ${Math.ceil(100 - ogtyAmount)} to  to unlock <b>10%</b> off your order.
            </div>
        </div>
        }
        else if(ogtyAmount >= 100 && ogtyAmount < 200) {
            return <div className='cart-bar'>
            {_renderProgressBar(ogtyAmount - 100)}
            <div className="shipping-to-free-message" style={{marginBottom:"30px"}}>
                 <b>Spend & Save:</b> You've qualified for <b>10% off</b>. Spend another <b>${ Math.ceil(200 - ogtyAmount)}</b> to  unlock <b>15% off</b> your order.
            </div>
        </div>
        }
        else if(ogtyAmount >= 200 && ogtyAmount < 400) {
            return <div className='cart-bar'>
            {_renderProgressBar((ogtyAmount - 200) / 200 *100)}
            <div className="shipping-to-free-message" style={{marginBottom:"30px"}}>
                 <b>Spend & Save:</b> You've qualified for <b>15% off</b>. Spend another ${ Math.ceil(400 - ogtyAmount)} to  unlock <b>20% off</b>  your order.
            </div>
        </div>
        }
        else {
            return <div className='cart-bar'>
            {_renderProgressBar(100)}
            <div className="shipping-to-free-message" style={{marginBottom:"30px"}}>
                 <b>Spend & Save:</b> You’ve reached our top tier—enjoy <b>20% off</b> your order.
            </div>
        </div>
        }
    }
    return null;

}

function _renderFreeShippingMessage(props: ICheckoutViewProps & ICheckoutProps<{}>): JSX.Element | null {
    const shippingAmount = props.data.checkout.result?.checkoutCart.cart.DeliveryModeChargeAmount || 0;
    const OrderSubTotal = (props.data.checkout.result?.checkoutCart.cart.AmountDue || 0) - shippingAmount;
    
    const freeShippingAmount = props.config.freeShippingAmount;

    if (!freeShippingAmount || !OrderSubTotal || OrderSubTotal >= freeShippingAmount) {
        return null;
    }
    return <div className="shipping-to-free-message">
        Spend an additional ${Math.ceil(freeShippingAmount - OrderSubTotal)} to get <b>FREE SHIPPING</b>
    </div>
}

function _renderSubTotal(props: ICheckoutViewProps& ICheckoutProps<{}>): JSX.Element | null {
   var subtotal=0;
   props.data.checkout.result?.checkoutCart.cart.CartLines?.forEach((item)=>{
    subtotal += item.Price! * item.Quantity!;
   });
    const OrderSubTotal = subtotal;//props.data.checkout.result?.checkoutCart.cart.NetPrice;
    return (
        <p className={`msc-order-summary__line-sub-total`}>
           {OrderSubTotal && OrderSubTotal > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.subTotalLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: OrderSubTotal }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}
function _renderTotalOrder(props: ICheckoutViewProps): JSX.Element | null {

    const OrderTotal = props.data.checkout.result?.checkoutCart.cart.TotalAmount;
    return (
        <p className={`msc-order-summary__line-order-total`}>
           {OrderTotal && OrderTotal > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.totalAmountLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: OrderTotal }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}
const _getCheckoutSummaryText = (props: ICheckoutViewProps & ICheckoutProps<{}>): React.ReactNode | undefined => {
    const { paragraph } = props.config;

    return (
        paragraph && (
            <MsDyn365.RichTextComponent
                text={paragraph}
                className='msc-order-summary__summary-text'
            />
        )
    );
};
const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutProps<{}>> = props => {
    const {
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton
    } = props;

    let isBusy = false;
    if(msdyn365Commerce.isBrowser) {
        const url: string = decodeURI(window.location.search);
        const urlParams: URLSearchParams = new URLSearchParams(url);
        if (urlParams.get('paymentMethod') === 'AP' && urlParams.has('orderToken')) {
            const orderToken = urlParams.get('orderToken');
            const afterpayStatus = urlParams.get('status') || 'FAILURE';

            if (afterpayStatus === 'SUCCESS' && orderToken) {
                isBusy = true;
            }
        }
    }

    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
    function _renderPlaceOrderCopy(): JSX.Element | null {
        return (
            <div className="place-order-copy">
                <span>By clicking Place order you agree to your signed terms of business, please contact your account manager for further information.</span>
            </div>
        )
    }

    return (
        <Module {...checkoutProps}>
            {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder &&
                <Node {...bodyProps} className={`${bodyProps.className} ${isBusy ? "checkout-isbusy" : "" }` }>
                    {loading}
                    {
                        isBusy ? (
                            <div className="afterpay-loading-wrapper">
                                <div className='afterpay-loading-wrapper-spinner' />   
                            </div> 
                        ) : null
                    }
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>
                                    {termsAndConditions}
                                </Node>
                                <Node {...mainControlProps}>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                                {isB2B? _renderPlaceOrderCopy() : null}
                            </Node>
                            <Node {...sideProps}>
                                {_renderOrderSummarylines(props)}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>
                                        {termsAndConditions}
                                    </Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                                {lineItems && <LineItemsComponent {...lineItems} />}
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>
                                        {termsAndConditions}
                                    </Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            }
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
